import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { CloudOff, Loader2 } from "lucide-react";
import AdminTreasuryChrome from "../treasury/AdminTreasuryChrome";
import TreasuryCoverageHero from "../treasury/TreasuryCoverageHero";
import TreasuryBreakdownPanel from "../treasury/TreasuryBreakdownPanel";
import TreasuryActivityPanel from "../treasury/TreasuryActivityPanel";
import {
  buildEscrowAnalytics,
  buildFloatHistorySeries,
  buildFlowWindows,
  buildLiabilityComposition,
  calcTreasuryCoverage,
} from "../treasury/treasuryHelpers";
import {
  fetchAdminProviderBalanceHistory,
  fetchAdminTreasuryOverview,
  refreshAdminProviderBalance,
} from "../treasury/api/fetchAdminTreasury";
import "../styles/admin-markets.css";
import "../treasury/admin-treasury.css";

export default function TreasuryPage() {
  const [overview, setOverview] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState(null);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAdminTreasuryOverview();
      setOverview(data);
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to load treasury overview");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const data = await fetchAdminProviderBalanceHistory({ page: 1, limit: 24 });
      setHistory(data.snapshots || []);
      setHistoryTotal(data.pagination?.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to load balance history");
    }
  }, []);

  useEffect(() => {
    loadOverview();
    loadHistory();
  }, [loadOverview, loadHistory]);

  const handleRefresh = () => {
    loadOverview();
    loadHistory();
  };

  const handleRecordBalance = async () => {
    setRecording(true);
    try {
      const data = await refreshAdminProviderBalance();
      setOverview((prev) => ({
        ...(prev || {}),
        generatedAt: data.generatedAt,
        userWallets: data.userWallets,
        escrow: data.escrow,
        paymentFlow: data.paymentFlow ?? prev?.paymentFlow,
        latestProviderSnapshot: data.snapshot,
      }));
      await loadHistory();
      toast.success("Balance recorded");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to record balance");
    } finally {
      setRecording(false);
    }
  };

  const wallets = overview?.userWallets;
  const escrow = overview?.escrow;
  const latest = overview?.latestProviderSnapshot;
  const paymentFlow = overview?.paymentFlow;
  const providerBalance = latest?.balance || 0;

  const coverage = useMemo(
    () =>
      calcTreasuryCoverage({
        providerBalance,
        userCash: wallets?.totalCash,
        lockedCash: escrow?.totalLockedCash,
        userCoin: wallets?.totalCoin,
        lockedCoin: escrow?.totalLockedCoin,
      }),
    [
      providerBalance,
      wallets?.totalCash,
      escrow?.totalLockedCash,
      wallets?.totalCoin,
      escrow?.totalLockedCoin,
    ]
  );

  const coverPct = coverage.ratio == null ? 0 : Math.min(100, Math.round(coverage.ratio * 100));

  const liabilitySegments = useMemo(
    () => buildLiabilityComposition({ wallets, escrow, coverage }),
    [wallets, escrow, coverage]
  );

  const escrowRows = useMemo(
    () => buildEscrowAnalytics(escrow?.breakdown),
    [escrow?.breakdown]
  );

  const flowWindows = useMemo(
    () => buildFlowWindows(paymentFlow),
    [paymentFlow]
  );

  const floatSeries = useMemo(
    () => buildFloatHistorySeries(history),
    [history]
  );

  return (
    <div className="am-page atrs-page">
      <div className="am-aurora" aria-hidden>
        <span className="am-aurora-a atrs-aurora" />
      </div>

      <AdminTreasuryChrome
        loading={loading}
        recording={recording}
        onRefresh={handleRefresh}
        onRecordBalance={handleRecordBalance}
      />

      <div className="atrs-desk">
        {error && !overview ? (
          <div className="atrs-idle">
            <CloudOff size={20} strokeWidth={1.8} />
            <p>{error}</p>
          </div>
        ) : null}

        {loading && !overview ? (
          <div className="atrs-idle">
            <Loader2 size={20} className="am-spin" />
            <p>Loading treasury analytics…</p>
          </div>
        ) : null}

        {overview ? (
          <div className="atrs-analytics">
            <TreasuryCoverageHero
              coverage={coverage}
              providerBalance={providerBalance}
              currency={latest?.currency}
              coverPct={coverPct}
              generatedAt={overview.generatedAt}
            />

            <div className="atrs-workspace">
              <TreasuryBreakdownPanel
                segments={liabilitySegments}
                total={coverage.obligations}
                escrowRows={escrowRows}
              />

              <TreasuryActivityPanel
                windows={flowWindows}
                series={floatSeries}
                totalSnapshots={historyTotal}
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
